import { Link } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import Swal from 'sweetalert2';
import { agentApi } from '@/services/agentApi';
import { authApi } from '@/services/authApi';
import { getLogoUrl } from '@/utils/logoUtils';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { 
  User, 
  Mail, 
  Phone, 
  Building2, 
  FileText, 
  Lock, 
  Eye, 
  EyeOff,
  Save,
  AlertCircle,
  Camera,
  X,
  CreditCard,
  Landmark,
  Bell,
  Download,
  Loader2
} from 'lucide-react';
import { exportMyData } from '@/services/dataExportApi';

export const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  // Check if user is a sub-account (has parentAgentId)
  const isSubAccount = user?.agentProfile?.parentAgentId !== undefined && user?.agentProfile?.parentAgentId !== null;
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  
  // Profile form state
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    agencyName: '',
    licenseNumber: '',
  });

  // Payout account details form state
  const [payoutForm, setPayoutForm] = useState({
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    routingNumber: '',
    swiftCode: '',
    accountType: 'CHECKING' as 'CHECKING' | 'SAVINGS',
    bankAddress: '',
    currency: 'NGN',
  });

  const [isPayoutLoading, setIsPayoutLoading] = useState(false);

  // Notification preferences state
  const [notificationPreferences, setNotificationPreferences] = useState({
    notifyOnSubAccountApplicationUpdates: false,
    isSubAccount: false,
  });
  const [isNotificationLoading, setIsNotificationLoading] = useState(false);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(true);

  // Password change form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [errors, setErrors] = useState<{
    profile?: string;
    password?: string;
    payout?: string;
    [key: string]: string | undefined;
  }>({});

  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Load notification preferences
  useEffect(() => {
    const loadNotificationPreferences = async () => {
      try {
        setIsLoadingPreferences(true);
        const prefs = await agentApi.getNotificationPreferences();
        setNotificationPreferences(prefs);
      } catch (error: any) {
        console.error('Failed to load notification preferences:', error);
        toast.error('Failed to load notification preferences');
      } finally {
        setIsLoadingPreferences(false);
      }
    };

    loadNotificationPreferences();
  }, []);

  // Initialize form with user data
  useEffect(() => {
    const initializeProfile = async () => {
      if (user) {
        // If agentProfile is not loaded, fetch it
        let agentProfileData = user.agentProfile;
        if (!agentProfileData && user.userType === 'AGENT') {
          try {
            const updatedUser = await authApi.getProfile();
            agentProfileData = updatedUser.agentProfile;
          } catch (error) {
            console.error('Failed to fetch agent profile:', error);
          }
        }

        // For sub-accounts, license number comes from parent agent (already set in backend)
        setProfileForm({
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          agencyName: agentProfileData?.agencyName || '',
          licenseNumber: agentProfileData?.licenseNumber || '',
        });

        // Initialize payout account details
        if (agentProfileData?.payoutAccountDetails) {
          const payoutDetails = agentProfileData.payoutAccountDetails;
          setPayoutForm({
            bankName: payoutDetails.bankName || '',
            accountHolderName: payoutDetails.accountHolderName || '',
            accountNumber: payoutDetails.accountNumber || '',
            routingNumber: payoutDetails.routingNumber || '',
            swiftCode: payoutDetails.swiftCode || '',
            accountType: payoutDetails.accountType || 'CHECKING',
            bankAddress: payoutDetails.bankAddress || '',
            currency: payoutDetails.currency || 'NGN',
          });
        }

        // Load profile picture
        if ((user as any).profilePicture) {
          setProfilePicture(getLogoUrl((user as any).profilePicture));
        } else {
          // Try to fetch profile picture
          try {
            const pictureUrl = await agentApi.getProfilePicture();
            if (pictureUrl) {
              setProfilePicture(getLogoUrl(pictureUrl));
            }
          } catch (error) {
            console.error('Failed to fetch profile picture:', error);
          }
        }
      }
    };

    initializeProfile();
  }, [user]);

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handlePayoutChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setPayoutForm(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const validateProfile = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!profileForm.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!profileForm.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Agency name is only required for main agents, not sub-accounts
    if (!isSubAccount && !profileForm.agencyName.trim()) {
      newErrors.agencyName = 'Agency name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!passwordForm.currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!passwordForm.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters long';
    }

    if (!passwordForm.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }

    setErrors(prev => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateProfile()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      // Update profile via agent API (includes firstName and lastName)
      // For sub-accounts, exclude agencyName and licenseNumber
      const updateData: any = {
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
      };
      
      // Only include agencyName and licenseNumber for main agents
      if (!isSubAccount) {
        updateData.agencyName = profileForm.agencyName;
        updateData.licenseNumber = profileForm.licenseNumber;
      }
      
      await agentApi.updateProfile(updateData);

      // Refresh user profile to get updated data
      await authApi.getProfile();
      
      toast.success('Profile updated successfully');
      
      // Note: The user context should be updated by the auth hook
      // For now, we'll do a simple reload to ensure UI is in sync
      // In a production app, you'd want to update the context directly
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error('Profile update error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update profile';
      setErrors({ profile: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }

    setIsPasswordLoading(true);
    setErrors({});

    try {
      // Call password change API
      await agentApi.changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });

      toast.success('Password changed successfully');
      
      // Reset password form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error: any) {
      console.error('Password change error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to change password';
      setErrors({ password: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }

      setProfilePictureFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePicturePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveProfilePicture = () => {
    setProfilePictureFile(null);
    setProfilePicturePreview(null);
  };

  const handleUploadProfilePicture = async () => {
    if (!profilePictureFile) {
      toast.error('Please select an image file');
      return;
    }

    setIsUploadingPicture(true);
    try {
      const pictureUrl = await agentApi.uploadProfilePicture(profilePictureFile);
      setProfilePicture(getLogoUrl(pictureUrl));
      setProfilePicturePreview(null);
      setProfilePictureFile(null);
      toast.success('Profile picture updated successfully');
      
      // Refresh user data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error('Profile picture upload error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to upload profile picture';
      toast.error(errorMessage);
    } finally {
      setIsUploadingPicture(false);
    }
  };

  const handlePayoutSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsPayoutLoading(true);
    setErrors({});

    try {
      await agentApi.updatePayoutAccountDetails(payoutForm);
      toast.success('Payout account details updated successfully');
      
      // Refresh user data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error('Payout account update error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update payout account details';
      setErrors({ payout: errorMessage });
      toast.error(errorMessage);
    } finally {
      setIsPayoutLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="row">
          <div className="col-12">
            <div className="d-flex align-items-center mb-4">
              <div className="p-3 rounded-3 bg-primary bg-opacity-10 me-3">
                <User size={24} className="text-primary" />
              </div>
              <div>
                <h2 className="mb-0 fw-bold">Profile Settings</h2>
                <p className="text-muted mb-0">Manage your account information</p>
                <Link className="small d-inline-block mt-1" to="/profile/documents">Agency documents →</Link>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          {/* Profile Picture Section */}
          <div className="col-lg-4 mb-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.05 }}
            >
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                  <h5 className="mb-0 fw-semibold">Profile Picture</h5>
                </div>
                <div className="card-body p-4 text-center">
                  <div className="position-relative d-inline-block mb-3">
                    <div
                      className="rounded-circle overflow-hidden"
                      style={{
                        width: '150px',
                        height: '150px',
                        backgroundColor: '#f0f0f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {profilePicturePreview ? (
                        <img
                          src={profilePicturePreview}
                          alt="Profile preview"
                          className="w-100 h-100 object-fit-cover"
                        />
                      ) : profilePicture ? (
                        <img
                          src={profilePicture}
                          alt="Profile"
                          className="w-100 h-100 object-fit-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='150' height='150'%3E%3Crect fill='%23ddd' width='150' height='150'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='50' dy='10.5' font-weight='bold' x='50%25' y='50%25' text-anchor='middle'%3E${(user?.firstName?.[0] || 'U').toUpperCase()}%3C/text%3E%3C/svg%3E`;
                          }}
                        />
                      ) : (
                        <div className="d-flex align-items-center justify-content-center w-100 h-100">
                          <User size={60} className="text-muted" />
                        </div>
                      )}
                    </div>
                    {profilePicturePreview && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger position-absolute top-0 end-0 rounded-circle"
                        style={{ width: '30px', height: '30px', padding: 0 }}
                        onClick={handleRemoveProfilePicture}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  
                  <div className="mb-3">
                    <input
                      type="file"
                      id="profilePictureInput"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      className="d-none"
                    />
                    <label
                      htmlFor="profilePictureInput"
                      className="btn btn-outline-primary btn-sm w-100 mb-2"
                      style={{ cursor: 'pointer' }}
                    >
                      <Camera size={16} className="me-2" />
                      {profilePicturePreview ? 'Change Picture' : 'Upload Picture'}
                    </label>
                  </div>

                  {profilePicturePreview && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm w-100"
                      onClick={handleUploadProfilePicture}
                      disabled={isUploadingPicture}
                    >
                      {isUploadingPicture ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Save size={16} className="me-2" />
                          Save Picture
                        </>
                      )}
                    </button>
                  )}

                  <small className="text-muted d-block mt-2">
                    JPG, PNG or GIF. Max size 5MB
                  </small>
                </div>
              </div>
            </motion.div>

            {/* Password Change Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              <div className="card border-0 shadow-sm mt-4">
                <div className="card-header bg-white border-bottom py-3">
                  <h5 className="mb-0 fw-semibold d-flex align-items-center">
                    <Lock size={20} className="me-2 text-primary" />
                    Change Password
                  </h5>
                </div>
                <div className="card-body p-4">
                  <form onSubmit={handlePasswordSubmit}>
                    {errors.password && (
                      <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
                        <AlertCircle size={20} className="me-2" />
                        <div>{errors.password}</div>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        Current Password <span className="text-danger">*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPasswords.current ? 'text' : 'password'}
                          className={`form-control ${errors.currentPassword ? 'is-invalid' : ''}`}
                          name="currentPassword"
                          value={passwordForm.currentPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3"
                          onClick={() => togglePasswordVisibility('current')}
                          style={{ border: 'none', background: 'none' }}
                        >
                          {showPasswords.current ? (
                            <EyeOff size={18} className="text-muted" />
                          ) : (
                            <Eye size={18} className="text-muted" />
                          )}
                        </button>
                      </div>
                      {errors.currentPassword && (
                        <div className="invalid-feedback">{errors.currentPassword}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">
                        New Password <span className="text-danger">*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPasswords.new ? 'text' : 'password'}
                          className={`form-control ${errors.newPassword ? 'is-invalid' : ''}`}
                          name="newPassword"
                          value={passwordForm.newPassword}
                          onChange={handlePasswordChange}
                          required
                          minLength={6}
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3"
                          onClick={() => togglePasswordVisibility('new')}
                          style={{ border: 'none', background: 'none' }}
                        >
                          {showPasswords.new ? (
                            <EyeOff size={18} className="text-muted" />
                          ) : (
                            <Eye size={18} className="text-muted" />
                          )}
                        </button>
                      </div>
                      {errors.newPassword && (
                        <div className="invalid-feedback">{errors.newPassword}</div>
                      )}
                      <small className="form-text text-muted">
                        Password must be at least 6 characters long
                      </small>
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold">
                        Confirm New Password <span className="text-danger">*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={showPasswords.confirm ? 'text' : 'password'}
                          className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                          name="confirmPassword"
                          value={passwordForm.confirmPassword}
                          onChange={handlePasswordChange}
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-link position-absolute end-0 top-50 translate-middle-y pe-3"
                          onClick={() => togglePasswordVisibility('confirm')}
                          style={{ border: 'none', background: 'none' }}
                        >
                          {showPasswords.confirm ? (
                            <EyeOff size={18} className="text-muted" />
                          ) : (
                            <Eye size={18} className="text-muted" />
                          )}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <div className="invalid-feedback">{errors.confirmPassword}</div>
                      )}
                    </div>

                    <div className="d-flex justify-content-end">
                      <button
                        type="submit"
                        className="btn btn-primary px-4"
                        disabled={isPasswordLoading}
                      >
                        {isPasswordLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Changing...
                          </>
                        ) : (
                          <>
                            <Lock size={18} className="me-2" />
                            Change Password
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Profile Information Section */}
          <div className="col-lg-4 mb-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                  <h5 className="mb-0 fw-semibold">Profile Information</h5>
                </div>
                <div className="card-body p-4">
                  <form onSubmit={handleProfileSubmit}>
                    {errors.profile && (
                      <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
                        <AlertCircle size={20} className="me-2" />
                        <div>{errors.profile}</div>
                      </div>
                    )}

                    <div className="row mb-3">
                      <div className="col-md-6 mb-3 mb-md-0">
                        <label className="form-label fw-semibold">
                          First Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                          name="firstName"
                          value={profileForm.firstName}
                          onChange={handleProfileChange}
                          required
                        />
                        {errors.firstName && (
                          <div className="invalid-feedback">{errors.firstName}</div>
                        )}
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Last Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                          name="lastName"
                          value={profileForm.lastName}
                          onChange={handleProfileChange}
                          required
                        />
                        {errors.lastName && (
                          <div className="invalid-feedback">{errors.lastName}</div>
                        )}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold d-flex align-items-center">
                        <Mail size={18} className="me-2 text-muted" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="form-control bg-light"
                        value={user.email || ''}
                        disabled
                        readOnly
                      />
                      <small className="form-text text-muted">
                        Email address cannot be changed
                      </small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold d-flex align-items-center">
                        <Phone size={18} className="me-2 text-muted" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        className="form-control bg-light"
                        value={user.phone || 'Not provided'}
                        disabled
                        readOnly
                      />
                      <small className="form-text text-muted">
                        Phone number cannot be changed
                      </small>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold d-flex align-items-center">
                        <Building2 size={18} className="me-2 text-muted" />
                        Agency Name {!isSubAccount && <span className="text-danger">*</span>}
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errors.agencyName ? 'is-invalid' : ''} ${isSubAccount ? 'bg-light' : ''}`}
                        name="agencyName"
                        value={profileForm.agencyName}
                        onChange={handleProfileChange}
                        required={!isSubAccount}
                        disabled={isSubAccount}
                        readOnly={isSubAccount}
                      />
                      {errors.agencyName && (
                        <div className="invalid-feedback">{errors.agencyName}</div>
                      )}
                      {isSubAccount && (
                        <small className="form-text text-muted">
                          Agency name cannot be changed for sub-accounts
                        </small>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold d-flex align-items-center">
                        <FileText size={18} className="me-2 text-muted" />
                        License Number
                      </label>
                      <input
                        type="text"
                        className={`form-control ${isSubAccount ? 'bg-light' : ''}`}
                        name="licenseNumber"
                        value={profileForm.licenseNumber}
                        onChange={handleProfileChange}
                        placeholder="Enter your license number"
                        disabled={isSubAccount}
                        readOnly={isSubAccount}
                      />
                      {isSubAccount && (
                        <small className="form-text text-muted">
                          License number is inherited from the main agent account and cannot be changed
                        </small>
                      )}
                    </div>

                    <div className="d-flex justify-content-end">
                      <button
                        type="submit"
                        className="btn btn-primary px-4"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save size={18} className="me-2" />
                            Save Changes
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Payout Account Details Section - Hidden for sub-accounts */}
          {!isSubAccount && (
            <div className="col-lg-4 mb-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                  <h5 className="mb-0 fw-semibold d-flex align-items-center">
                    <Landmark size={20} className="me-2 text-primary" />
                    Payout Account Details
                  </h5>
                </div>
                <div className="card-body p-4">
                  <form onSubmit={handlePayoutSubmit}>
                    {errors.payout && (
                      <div className="alert alert-danger d-flex align-items-center mb-4" role="alert">
                        <AlertCircle size={20} className="me-2" />
                        <div>{errors.payout}</div>
                      </div>
                    )}

                    <div className="row mb-3">
                      <div className="col-md-6 mb-3 mb-md-0">
                        <label className="form-label fw-semibold">
                          Bank Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="bankName"
                          value={payoutForm.bankName}
                          onChange={handlePayoutChange}
                          placeholder="Enter bank name"
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Account Holder Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="accountHolderName"
                          value={payoutForm.accountHolderName}
                          onChange={handlePayoutChange}
                          placeholder="Enter account holder name"
                          required
                        />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6 mb-3 mb-md-0">
                        <label className="form-label fw-semibold">
                          Account Number <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="accountNumber"
                          value={payoutForm.accountNumber}
                          onChange={handlePayoutChange}
                          placeholder="Enter account number"
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Account Type <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select"
                          name="accountType"
                          value={payoutForm.accountType}
                          onChange={handlePayoutChange}
                          required
                        >
                          <option value="CHECKING">Checking</option>
                          <option value="SAVINGS">Savings</option>
                        </select>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6 mb-3 mb-md-0">
                        <label className="form-label fw-semibold">
                          Routing Number / Sort Code
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="routingNumber"
                          value={payoutForm.routingNumber}
                          onChange={handlePayoutChange}
                          placeholder="Enter routing number or sort code"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          SWIFT / BIC Code
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="swiftCode"
                          value={payoutForm.swiftCode}
                          onChange={handlePayoutChange}
                          placeholder="Enter SWIFT or BIC code"
                        />
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6 mb-3 mb-md-0">
                        <label className="form-label fw-semibold">
                          Currency
                        </label>
                        <select
                          className="form-select"
                          name="currency"
                          value={payoutForm.currency}
                          onChange={handlePayoutChange}
                        >
                          <option value="NGN">NGN - Nigerian Naira</option>
                          <option value="USD">USD - US Dollar</option>
                          <option value="EUR">EUR - Euro</option>
                          <option value="GBP">GBP - British Pound</option>
                          <option value="AUD">AUD - Australian Dollar</option>
                          <option value="CAD">CAD - Canadian Dollar</option>
                          <option value="NZD">NZD - New Zealand Dollar</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Bank Address
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="bankAddress"
                          value={payoutForm.bankAddress}
                          onChange={handlePayoutChange}
                          placeholder="Enter bank address (optional)"
                        />
                      </div>
                    </div>

                    <div className="alert alert-info d-flex align-items-start mb-4" role="alert">
                      <CreditCard size={20} className="me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Security Notice:</strong> Your payout account details are encrypted and stored securely. 
                        This information is only used for processing commission payouts.
                      </div>
                    </div>

                    <div className="d-flex justify-content-end">
                      <button
                        type="submit"
                        className="btn btn-primary px-4"
                        disabled={isPayoutLoading}
                      >
                        {isPayoutLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save size={18} className="me-2" />
                            Save Payout Details
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </motion.div>
          </div>
          )}

          {/* Notification Preferences Section - Only for main agents */}
          {!isSubAccount && (
            <div className="col-lg-4 mb-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
              >
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white border-bottom py-3">
                    <h5 className="mb-0 fw-semibold d-flex align-items-center">
                      <Bell size={20} className="me-2 text-primary" />
                      Notification Preferences
                    </h5>
                  </div>
                  <div className="card-body p-4">
                    {isLoadingPreferences ? (
                      <div className="text-center py-4">
                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : (
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        try {
                          setIsNotificationLoading(true);
                          await agentApi.updateNotificationPreferences({
                            notifyOnSubAccountApplicationUpdates: notificationPreferences.notifyOnSubAccountApplicationUpdates,
                          });
                          toast.success('Notification preferences updated successfully');
                        } catch (error: any) {
                          console.error('Failed to update notification preferences:', error);
                          toast.error(error.response?.data?.error || 'Failed to update notification preferences');
                        } finally {
                          setIsNotificationLoading(false);
                        }
                      }}>
                        <div className="mb-4">
                          <div className="form-check form-switch">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="notifyOnSubAccountApplicationUpdates"
                              checked={notificationPreferences.notifyOnSubAccountApplicationUpdates}
                              onChange={(e) => {
                                setNotificationPreferences({
                                  ...notificationPreferences,
                                  notifyOnSubAccountApplicationUpdates: e.target.checked,
                                });
                              }}
                              disabled={isNotificationLoading}
                            />
                            <label className="form-check-label fw-semibold" htmlFor="notifyOnSubAccountApplicationUpdates">
                              Receive notifications for sub-account application updates
                            </label>
                          </div>
                          <small className="text-muted d-block mt-2">
                            When enabled, you will receive email and in-app notifications whenever the status of an application 
                            managed by one of your sub-accounts is updated by an admin.
                          </small>
                        </div>

                        <div className="alert alert-info d-flex align-items-start mb-4" role="alert">
                          <Bell size={20} className="me-2 mt-1 flex-shrink-0" />
                          <div>
                            <strong>Note:</strong> Sub-accounts will always receive notifications for their own applications. 
                            This setting only controls whether you, as the main agent, also receive notifications.
                          </div>
                        </div>

                        <div className="d-flex justify-content-end">
                          <button
                            type="submit"
                            className="btn btn-primary px-4"
                            disabled={isNotificationLoading}
                          >
                            {isNotificationLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Saving...
                              </>
                            ) : (
                              <>
                                <Save size={18} className="me-2" />
                                Save Preferences
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {/* Data & privacy - Download my data (GDPR) */}
          <div className="col-12 mt-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white border-bottom py-3">
                  <h5 className="mb-0 fw-semibold d-flex align-items-center">
                    <Download size={20} className="me-2 text-primary" />
                    Data &amp; privacy
                  </h5>
                </div>
                <div className="card-body p-4">
                  <p className="text-muted small mb-2">
                    Download a copy of your personal data (profile, applications metadata, and more) in a portable format. One export per 24 hours.
                  </p>
                  {exportError && (
                    <div className="alert alert-warning py-2 small mb-2" role="alert">
                      {exportError}
                    </div>
                  )}
                  {deleteError && (
                    <div className="alert alert-danger py-2 small mb-2" role="alert">
                      {deleteError}
                    </div>
                  )}
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={async () => {
                        setExportLoading(true);
                        setExportError(null);
                        try {
                          const data = await exportMyData();
                          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `wsss-data-export-${new Date().toISOString().slice(0, 10)}.json`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          window.URL.revokeObjectURL(url);
                          toast.success('Your data has been downloaded.');
                        } catch (err: any) {
                          const msg = err?.message || 'Failed to export data';
                          const retrySec = err?.retryAfterSeconds;
                          setExportError(retrySec ? `${msg} You can try again in ${Math.ceil(retrySec / 60)} minutes.` : msg);
                          toast.error(msg);
                        } finally {
                          setExportLoading(false);
                        }
                      }}
                      disabled={exportLoading}
                    >
                      {exportLoading ? (
                        <>
                          <Loader2 size={18} className="me-2 animate-spin" />
                          Preparing...
                        </>
                      ) : (
                        <>
                          <Download size={18} className="me-2" />
                          Download my data
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={async () => {
                        const fullName = user
                          ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                          : '';

                        const result = await Swal.fire({
                          title: 'Delete your agent account?',
                          html: fullName
                            ? `<p>This will permanently delete your agent account and associated data where legally allowed. This action cannot be undone.</p><p class=\"mt-2\">To confirm, type <strong>${fullName}</strong> below.</p>`
                            : '<p>This will permanently delete your agent account and associated data where legally allowed. This action cannot be undone.</p><p class=\"mt-2\">To confirm, type your full name below.</p>',
                          icon: 'warning',
                          showCancelButton: true,
                          confirmButtonColor: '#dc3545',
                          cancelButtonColor: '#6c757d',
                          confirmButtonText: 'Yes, delete my account',
                          cancelButtonText: 'Cancel',
                          reverseButtons: true,
                          input: 'text',
                          inputPlaceholder: 'Enter your full name',
                          inputValidator: (value) => {
                            if (!value) {
                              return 'Please enter your full name to confirm.';
                            }
                            if (fullName && value.trim() !== fullName) {
                              return 'Name does not match. Please type your full name exactly as shown.';
                            }
                            return null;
                          },
                        });

                        if (!result.isConfirmed) return;

                        setDeleteLoading(true);
                        setDeleteError(null);
                        try {
                          const response = await fetch(
                            `${import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1'}/users/me`,
                            {
                              method: 'DELETE',
                              headers: {
                                Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
                              },
                            }
                          );

                          const body = await response.json().catch(() => ({}));
                          if (!response.ok) {
                            const message =
                              body.error || 'Failed to delete account. Please try again or contact support.';
                            setDeleteError(message);
                            toast.error(message);
                            return;
                          }

                          toast.success('Your account has been deleted.');
                          localStorage.removeItem('token');
                          localStorage.removeItem('refreshToken');
                          window.location.href = '/login';
                        } catch (err: any) {
                          const msg = err?.message || 'Failed to delete account. Please try again.';
                          setDeleteError(msg);
                          toast.error(msg);
                        } finally {
                          setDeleteLoading(false);
                        }
                      }}
                      disabled={deleteLoading}
                    >
                      {deleteLoading ? (
                        <>
                          <Loader2 size={18} className="me-2 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>Delete my account</>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

